import { BACKEND_URL, BACKEND_URL_FALLBACK } from '../config/backendConfig';

/**
 * Auth API service
 * Handles all authentication-related API calls
 */

const AUTH_CLIENT = 'web';

const getBackendUrl = () => {
  // Use primary URL, fallback will be handled in fetch calls
  return BACKEND_URL;
};

const withClient = (body = {}) => ({ ...body, client: AUTH_CLIENT });

/**
 * Sign up a new user
 */
export const signup = async (userData) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(withClient(userData))
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'Signup failed');
  }

  return data;
};

/**
 * Login user
 */
export const login = async (username, password) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(withClient({ username, password }))
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'Login failed');
  }

  return data;
};

/**
 * Verify token and get user info
 */
export const verifyToken = async (token) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/verify`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Token verification failed');
  }

  return data;
};

/**
 * Request password reset - send OTP to email
 */
export const forgotPassword = async (email) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to send reset code');
  }

  return data;
};

/**
 * Verify OTP code
 */
export const verifyOtp = async (email, otp) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/verify-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, otp })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'OTP verification failed');
  }

  return data;
};

/**
 * Reset password using reset token
 */
export const resetPassword = async (resetToken, newPassword) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ resetToken, newPassword })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Password reset failed');
  }

  return data;
};

/**
 * Google login - send Google access token to backend
 */
export const googleLogin = async (accessToken) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/google`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(withClient({ access_token: accessToken }))
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Google login failed');
  }

  return data;
};

/**
 * Apple login - send Apple identity token to backend
 */
export const appleLogin = async (identityToken, fullName) => {
  const backendUrl = getBackendUrl();
  const body = withClient({ identityToken });
  if (fullName && (fullName.firstName || fullName.lastName)) {
    body.fullName = fullName;
  }
  const response = await fetch(`${backendUrl}/api/auth/apple`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Apple login failed');
  }
  return data;
};

/**
 * Resend OTP code
 */
export const resendOtp = async (email) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/resend-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to resend code');
  }

  return data;
};

export const sendLoginOtp = async (phone) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/send-login-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to send OTP');
  return data;
};

export const verifyLoginOtp = async (phone, otp) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/verify-login-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withClient({ phone, otp }))
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'OTP verification failed');
  return data;
};

export const loginMobile = async (phone, password) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/login-mobile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withClient({ phone, password }))
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Login failed');
  return data;
};

/**
 * Refresh access token using stored refresh token (app / future web).
 */
export const refreshSession = async (refreshToken) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withClient({ refreshToken })),
  });
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.error || 'Session refresh failed');
    err.code = data.code;
    throw err;
  }
  return data;
};

/**
 * Server-side logout — revoke refresh token when present.
 */
export const logoutSession = async ({ refreshToken, allDevices = false } = {}) => {
  const backendUrl = getBackendUrl();
  const access = localStorage.getItem('yovo_auth_token');
  const headers = { 'Content-Type': 'application/json' };
  if (access) headers.Authorization = `Bearer ${access}`;
  try {
    await fetch(`${backendUrl}/api/auth/logout`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken: refreshToken || undefined, allDevices }),
    });
  } catch (_) {
    /* best-effort */
  }
};

export const addPhone = async (phone, token) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/add-phone`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ phone })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to send OTP');
  return data;
};

export const verifyPhone = async (phone, otp, token) => {
  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/api/auth/verify-phone`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ phone, otp })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Phone verification failed');
  return data;
};

